import { Calendar, Check, Plus } from "lucide-react";
import { type ReactNode, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAllWeeks } from "@/hooks/useWeeks";
import { addDays, isoWeekMonday, isoWeekOf, weekRangeLabel } from "@/lib/days";
import { createWeek } from "@/lib/repos/weeks";
import type { SettingsData, WeekData } from "@/lib/schema";
import { Modal } from "./Modal";

const WEEK_OFFSETS = [-1, 0, 1, 2, 3] as const;

interface Props {
  open: boolean;
  onClose: () => void;
  settings: SettingsData;
}

type WeekState = WeekData["state"];

interface WeekOption {
  year: number;
  weekNumber: number;
  monday: string;
  label: string;
  /** The state of the week already holding this ISO week, if any. */
  takenBy: WeekState | null;
}

const TAKEN_HINT: Record<WeekState, string> = {
  open: "finnes",
  completed: "fullført",
};

/** One icon per row: + to create, otherwise the state already holding it. */
const OPTION_ICON: Record<WeekState | "free", ReactNode> = {
  free: <Plus />,
  open: <Calendar />,
  completed: <Check />,
};

/**
 * Every week holds its ISO week, completed ones included — at most one week
 * per ISO week (CONTEXT.md, Week).
 */
function takenSlots(
  weeks: { data: { year: number; weekNumber: number; state: WeekState } }[],
): Map<string, WeekState> {
  return new Map(
    weeks.map((w) => [`${w.data.year}-${w.data.weekNumber}`, w.data.state]),
  );
}

function buildOptions(
  today: Date,
  taken: Map<string, WeekState>,
): WeekOption[] {
  const { year, weekNumber } = isoWeekOf(today);
  const thisMonday = isoWeekMonday(year, weekNumber);
  return WEEK_OFFSETS.map((offset) => {
    const monday = addDays(thisMonday, -offset * 7);
    const [y, m, d] = monday.split("-").map(Number);
    const iso = isoWeekOf(new Date(Date.UTC(y, m - 1, d)));
    const label = `Uke ${iso.weekNumber}, ${iso.year} · ${weekRangeLabel(monday)}`;
    const key = `${iso.year}-${iso.weekNumber}`;
    return {
      year: iso.year,
      weekNumber: iso.weekNumber,
      monday,
      label,
      takenBy: taken.get(key) ?? null,
    };
  });
}

export function NewWeekModal({ open, onClose, settings }: Props) {
  const navigate = useNavigate();
  const { weeks } = useAllWeeks();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const taken = useMemo(() => takenSlots(weeks), [weeks]);

  const options = useMemo(() => buildOptions(new Date(), taken), [taken]);

  useEffect(() => {
    if (!open) setError(null);
  }, [open]);

  const pick = async (opt: WeekOption) => {
    if (opt.takenBy || submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      const week = await createWeek({
        year: opt.year,
        weekNumber: opt.weekNumber,
        startDate: opt.monday,
        settings,
      });
      const today = new Date();
      const todayIso = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}-${String(today.getDate()).padStart(2, "0")}`;
      const lastDay = addDays(opt.monday, 6);
      const inRange = todayIso >= opt.monday && todayIso <= lastDay;
      if (inRange) {
        const idx = Math.floor(
          (Date.UTC(today.getFullYear(), today.getMonth(), today.getDate()) -
            Date.UTC(
              Number(opt.monday.slice(0, 4)),
              Number(opt.monday.slice(5, 7)) - 1,
              Number(opt.monday.slice(8, 10)),
            )) /
            86400000,
        );
        const keys = ["mon", "tue", "wed", "thu", "fri", "sat", "sun"];
        const dayKey = keys[idx] ?? "mon";
        navigate(`/uke/${encodeURIComponent(week.id)}/${dayKey}`);
      } else {
        navigate(`/uke/${encodeURIComponent(week.id)}`);
      }
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal open={open} onClose={onClose} data-testid="new-week-modal">
      <h2>Velg uke</h2>
      <ul className="option-list">
        {options.map((opt) => (
          <li key={`${opt.year}-${opt.weekNumber}`}>
            <button
              type="button"
              className={opt.takenBy ? "option taken" : "option"}
              disabled={opt.takenBy !== null || submitting}
              onClick={() => pick(opt)}
              data-testid={`week-option-${opt.year}-${opt.weekNumber}`}
            >
              {opt.label}
              {opt.takenBy ? (
                <span
                  className="hint"
                  data-testid={`week-option-hint-${opt.year}-${opt.weekNumber}`}
                >
                  {" "}
                  · {TAKEN_HINT[opt.takenBy]}
                </span>
              ) : null}
              <span
                className={`option-icon ${opt.takenBy ?? "free"}`}
                aria-hidden="true"
                data-testid={`week-option-icon-${opt.takenBy ?? "free"}-${opt.year}-${opt.weekNumber}`}
              >
                {OPTION_ICON[opt.takenBy ?? "free"]}
              </span>
            </button>
          </li>
        ))}
      </ul>
      {error ? (
        <p className="error" data-testid="new-week-error">
          {error}
        </p>
      ) : null}
      <div className="modal-actions">
        <button
          type="button"
          className="btn ghost"
          onClick={onClose}
          data-testid="new-week-cancel"
        >
          Avbryt
        </button>
      </div>
    </Modal>
  );
}

export function freeWeekCandidates(
  weeks: { data: { year: number; weekNumber: number; state: WeekState } }[],
): { year: number; weekNumber: number }[] {
  return buildOptions(new Date(), takenSlots(weeks))
    .filter((o) => o.takenBy === null)
    .map((o) => ({ year: o.year, weekNumber: o.weekNumber }));
}
